import * as React from 'react';
import HeaderMenuItem from "./HeaderMenuItem";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {DropDownRouteData, HeaderMenuData} from "../../../../navigation/route/HeaderRoutes";
import {connect} from "react-redux";
import User from "../../../../model/user/User";
import {RootState} from "../../../../store/reducer/root/RootState";
import "./headerMenu.scss"

interface ReduxProps {
	/**
	 * Logged in user.
	 */
	user: User
}

interface ComponentProps {
	/**
	 * Header menu data.
	 */
	data: HeaderMenuData[];
	/**
	 * Indicates if the menu is folded behind a button on a narrow screen. There is no hover on a touch
	 * screen, so a click opens a submenu instead.
	 */
	compact?: boolean;
	/**
	 * Called when an item takes the user somewhere, so a folded menu can close.
	 */
	onNavigate?: () => void;
}

interface State {
	/**
	 * Id of menu item that have drop down expanded..
	 */
	expandedItemId: number;
}

type Props = ComponentProps & ReduxProps & RouteComponentProps<ComponentProps>;

class HeaderMenu extends React.Component<Props, State> {

	/**
	 * Timestamp of last item being expanded.
	 */
	lastItemExpandedDateTime: Date;

	constructor(props: Props) {
		super(props);
		this.onMenuItemExpand = this.onMenuItemExpand.bind(this);
		this.onMenuItemCollapse = this.onMenuItemCollapse.bind(this);
		this.onDropDownLinkClick = this.onDropDownLinkClick.bind(this);
		this.onNonExpandableItemClick = this.onNonExpandableItemClick.bind(this);
		this.state = {expandedItemId: null};
	}

	render() {
		return (
			<ul className="c-header-menu">
				{this.renderMenu()}
			</ul>
		);
	}

	/**
	 * Renders menu.
	 */
	renderMenu() {
		return this.props.data.map((route, index) => {
			if (!this.props.user.hasPermissions(route.allowedPermissions)) {
				return null;
			}

			return (
				<HeaderMenuItem route={route}
								key={index}
								isActive={this.isRouteActive(route.route)}
								isExpanded={this.state.expandedItemId == index}
								itemId={index}
								onMouseOver={this.props.compact ? noop : this.onMenuItemExpand}
								onMouseOut={this.props.compact ? noop : this.onMenuItemCollapse}
								onToggle={this.props.compact ? this.onMenuItemToggle : undefined}
								onClick={this.onDropDownLinkClick}
								clickableItem={!route.subRoutes}
								onNonExpandableItemClick={this.onNonExpandableItemClick}/>
			)
		})
	}

	/**
	 * Handler for non-expandable menu item
	 * @param {string} route
	 */
	onNonExpandableItemClick(route: string) {
		this.props.history.push(route);
		this.props.onNavigate && this.props.onNavigate();
	}

	/**
	 * Opens the item's submenu, or closes it when it is the open one.
	 */
	onMenuItemToggle = (itemId: number) => {
		this.setState({expandedItemId: this.state.expandedItemId === itemId ? null : itemId});
	}

	/**
	 * Indicates if location path contains specified route name.
	 * @param route
	 */
	isRouteActive(route: string): boolean {
		return this.props.location && this.props.location.pathname.indexOf(route) >= 0;
	}

	/**
	 * Handler form menu item drop-down expand.
	 */
	onMenuItemExpand(itemId: number) {
		this.setState({expandedItemId: itemId});
		this.lastItemExpandedDateTime = new Date();
	}

	/**
	 * Handler for menu item drop-down collapse.
	 */
	onMenuItemCollapse() {
		const previousDate = this.lastItemExpandedDateTime;
		setTimeout(() => {
			if (previousDate === this.lastItemExpandedDateTime) {
				this.setState({expandedItemId: null});
				this.lastItemExpandedDateTime = null;
			}
		}, 500)
	}

	/**
	 * Drop down menu item click handler.
	 * @param {string} url Url to redirect to.
	 */
	onDropDownLinkClick(url: string) {
		this.props.history.push(url);
		this.setState({expandedItemId: null});
		this.props.onNavigate && this.props.onNavigate();
	}
}

const noop = () => {};

function mapStateToProps(state: RootState) {
	return {
		user: state.user.user.get
	}
}

export default withRouter<ComponentProps>(connect(mapStateToProps)(HeaderMenu));